import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'

import { useListData, usePet } from '../../modules'

const appearance = {
    draft: {
        label: 'Draft',
        classes: 'bg-slate-100 text-slate-700 border-slate-200',
        description: 'Claim is initialized but not yet submitted.'
    },
    submitted: {
        label: 'Submitted',
        classes: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        description: 'Claim has been sent and is awaiting insurer response.'
    },
    accepted: {
        label: 'Accepted',
        classes: 'bg-green-100 text-green-700 border-green-200',
        description: 'Claim has been accepted by the insurer.'
    },
    rejected: {
        label: 'Rejected',
        classes: 'bg-red-100 text-red-700 border-red-200',
        description: 'Claim was rejected and needs follow-up.'
    }
}

const ClaimStatusCard = ({ claimId, admissionId, orderId }) => {
    const [claim, setClaim] = useState()
    const [rejectionReason, setRejectionReason] = useState('')
    const [discardReasonType, setDiscardReasonType] = useState('')
    const [discardReasonText, setDiscardReasonText] = useState('')
    const [payloadPreview, setPayloadPreview] = useState()
    const [submissionPlan, setSubmissionPlan] = useState()
    const [consentStatus, setConsentStatus] = useState()
    const [patientString, setPatientString] = useState('')
    const [selectedPatient, setSelectedPatient] = useState(null)
    const [emergencyOtp, setEmergencyOtp] = useState('')
    const [unknownReason, setUnknownReason] = useState('SHA_UNREGISTERED')
    const [protocols, setProtocols] = useState([])
    const [selectedProtocol, setSelectedProtocol] = useState(null)
    const [protocolQuantity, setProtocolQuantity] = useState('1')
    const [protocolUnitPrice, setProtocolUnitPrice] = useState('')
    const scopeLabel = admissionId ? 'admission' : orderId ? 'bill' : 'visit'
    const emergencyInterventionCode = claim?.emergency?.interventionCodes?.[0] ?? ''

    const [{ loading, error }, fetchClaim] = usePet('claimStatus', {
        id: claimId,
        admission: admissionId,
        order: orderId,
        ensure: !claimId
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => setClaim(data)
    })

    const [{ loading: previewLoading }, fetchPayloadPreview] = usePet('claimPayloadPreview', {
        id: claimId,
        admission: admissionId,
        order: orderId,
        ensure: !claimId
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => {
            setPayloadPreview(data?.payload)
            setSubmissionPlan(data?.submissionPlan)
        }
    })

    const [{ loading: transitioning }, transitionClaim] = usePet('transitionClaimStatus', {
        id: claim?._id,
        status: claim?.status === 'draft' ? 'submitted' : claim?.status === 'submitted' ? 'accepted' : undefined,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Recording payer acceptance...' },
        callback: ({ data }) => setClaim(data)
    })

    const [{ loading: executing }, executeClaimSubmission] = usePet('executeClaimSubmission', {
        id: claim?._id,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Executing claim submission plan...' },
        callback: ({ data }) => {
            setClaim(data?.claim)
            fetchPayloadPreview()
        }
    })

    const [{ loading: rejecting }, rejectClaim] = usePet('transitionClaimStatus', {
        id: claim?._id,
        status: 'rejected',
        reason: rejectionReason,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Recording payer rejection...' },
        callback: ({ data }) => {
            setClaim(data)
            setRejectionReason('')
        }
    })

    const [{ loading: reopening }, reopenClaim] = usePet('transitionClaimStatus', {
        id: claim?._id,
        status: 'draft',
        reason: 'Returned to draft for correction and resubmission',
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Returning claim to draft...' },
        callback: ({ data }) => {
            setClaim(data)
            fetchPayloadPreview()
        }
    })

    const [{ loading: discarding }, discardClaim] = usePet('discardClaim', {
        id: claim?._id,
        cancelReasonType: discardReasonType,
        cancelReasonText: discardReasonText,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Discarding SHA claim...' },
        callback: ({ data }) => {
            setClaim(data?.claim)
            setDiscardReasonType('')
            setDiscardReasonText('')
            fetchPayloadPreview()
        }
    })

    const [{ loading: refreshingConsent }, refreshClaimConsent] = usePet('refreshClaimConsent', {
        session: claim?.consentSession?.id,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Refreshing SHA consent...' },
        callback: () => {
            fetchClaim()
            setConsentStatus(null)
        }
    })

    const [{ loading: closingConsent }, closeClaimConsent] = usePet('closeClaimConsent', {
        session: claim?.consentSession?.id,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Closing SHA consent...' },
        callback: () => {
            fetchClaim()
            setConsentStatus(null)
        }
    })

    const [{ loading: checkingConsent }, checkConsentStatus] = usePet('claimOtpSessionStatus', {
        session: claim?.consentSession?.id,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Checking SHA consent status...' },
        callback: ({ data }) => setConsentStatus(data)
    })

    const [
        { loading: patientsLoading },
        { list: patients = [] },
    ] = useListData('patients', { string: patientString }, { notify: false }, 'patients', 20)

    const [{ loading: protocolsLoading }, fetchEmergencyProtocols] = usePet('emergencyProtocols', {
        interventionCode: emergencyInterventionCode,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Fetching SHA emergency protocols...' },
        callback: ({ data }) => {
            const options = (data?.protocols ?? []).map(protocol => ({
                value: protocol?.protocol_code,
                label: `${protocol?.protocol_code}${protocol?.unit_price !== undefined ? ` • Ksh. ${protocol.unit_price}` : ''}`,
                ...protocol,
            }))
            setProtocols(options)
            if (!selectedProtocol && options.length > 0) {
                setSelectedProtocol(options[0])
                if (options[0]?.unit_price !== undefined) setProtocolUnitPrice(String(options[0].unit_price))
            }
        }
    })

    const [{ loading: identifyingEmergency }, identifyEmergencyPatient] = usePet('identifyEmergencyClaimPatient', {
        id: claim?._id,
        patient: selectedPatient?.value,
        otp: emergencyOtp,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Linking patient to emergency claim...' },
        callback: ({ data }) => {
            setClaim(data?.claim)
            setSelectedPatient(null)
            setEmergencyOtp('')
            fetchPayloadPreview()
        }
    })

    const [{ loading: updatingUnknownReason }, updateUnknownReason] = usePet('setEmergencyUnknownReason', {
        id: claim?._id,
        reasonForUnknownPatient: unknownReason,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Saving emergency patient status...' },
        callback: ({ data }) => {
            setClaim(data)
            fetchPayloadPreview()
        }
    })

    const [{ loading: addingProtocol }, addEmergencyProtocolLine] = usePet('addEmergencyProtocolLine', {
        id: claim?._id,
        protocolCode: selectedProtocol?.protocol_code ?? selectedProtocol?.value,
        interventionCode: selectedProtocol?.intervention_code ?? emergencyInterventionCode,
        unitPrice: protocolUnitPrice,
        quantity: protocolQuantity,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Adding emergency protocol line...' },
        callback: ({ data }) => {
            setClaim(data?.claim)
            fetchPayloadPreview()
        }
    })

    const [{ loading: resendingDoctorConsent }, resendEmergencyDoctorConsent] = usePet('resendEmergencyDoctorConsent', {
        id: claim?._id,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Requesting emergency doctor consent...' },
        callback: ({ data }) => {
            setClaim(data?.claim)
            fetchPayloadPreview()
        }
    })

    useEffect(() => {
        if (!claimId && !admissionId && !orderId) return

        fetchClaim()
        fetchPayloadPreview()

        const intervalId = setInterval(() => {
            fetchClaim()
            fetchPayloadPreview()
        }, 45000)

        return () => clearInterval(intervalId)
    }, [claimId, admissionId, orderId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!claim?._id || claim?.serviceType !== 'EMERGENCY') return
        if (!emergencyInterventionCode) return

        fetchEmergencyProtocols()
    }, [claim?._id, claim?.serviceType, emergencyInterventionCode]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setUnknownReason(claim?.emergency?.reasonForUnknownPatient ?? 'SHA_UNREGISTERED')
    }, [claim?.emergency?.reasonForUnknownPatient])

    const patientOptions = useMemo(() =>
        patients.map(patient => ({
            value: patient?._id,
            label: `${patient?.name}${patient?.beneficiaryCrId ? ` • ${patient.beneficiaryCrId}` : ''}${patient?.identification ? ` • ${patient.identification}` : ''}`,
            ...patient,
        })),
    [patients])

    if (!claimId && !admissionId && !orderId) return null

    if (loading && !claim) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 py-4">
                <p className="font-medium text-sm mb-1">Insurance Claim</p>
                <p className="text-sm text-gray-500 mb-0">Checking claim status...</p>
            </div>
        )
    }

    if (error && !claim) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-red-200 bg-red-50 px-4 py-4">
                <p className="font-medium text-sm mb-1 text-red-700">Insurance Claim</p>
                <p className="text-sm text-red-600 mb-0">Unable to load claim status right now.</p>
            </div>
        )
    }

    if (!claim) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 py-4">
                <p className="font-medium text-sm mb-1">Insurance Claim</p>
                <p className="text-sm text-gray-500 mb-0">No SHA claim is linked to this {scopeLabel}.</p>
            </div>
        )
    }

    const state = appearance[claim?.status] ?? appearance.draft
    const canSubmit = claim?.status === 'draft'
    const canResolve = claim?.status === 'submitted'
    const missingPreauth = claim?.preauthSummary?.missing ?? []
    const unapprovedPreauth = claim?.preauthSummary?.unapproved ?? []
    const hasPreauthBlockers = missingPreauth.length > 0 || unapprovedPreauth.length > 0
    const lineSummary = claim?.lineSummary
    const missingCodes = lineSummary?.missingRegulatoryCodes ?? []
    const diagnoses = lineSummary?.diagnoses ?? []
    const hasCodeBlockers = missingCodes.length > 0
    const attachmentSummary = claim?.attachmentSummary
    const executionSummary = claim?.executionSummary
    const perDiemSummary = claim?.perDiemSummary
    const recommendedMissingAttachments = attachmentSummary?.recommendedMissingTypes ?? []
    const taggedAttachments = attachmentSummary?.attachments?.filter(attachment => attachment?.shaDocumentType) ?? []
    const planSteps = submissionPlan?.steps ?? []
    const hasDiagnosisBlockers = Boolean(lineSummary?.diagnosisGap) || (payloadPreview?.diagnoses?.length ?? 0) === 0
    const hasAttachmentBlockers = recommendedMissingAttachments.length > 0 || (payloadPreview?.attachments?.length ?? 0) === 0
    const consentSession = claim?.consentSession
    const consentVisitOpen = Boolean(consentSession?.visitId) && !consentSession?.used
    const consentStatusLabel = consentStatus?.consent_status ?? (consentSession?.validated ? 'Approved' : consentSession?.used ? 'Closed' : 'Pending')
    const consentMethod = consentSession?.consentMethod ?? 'otp'
    const visitConsentStep = planSteps.find(step => step.key === 'visit_consent')
    const closeClaimStep = planSteps.find(step => step.key === 'close_claim')
    const hasVisitStartOtp = Boolean(claim?.visitStartOtp)
    const hasDischargeOtp = Boolean(claim?.dischargeOtp)
    const hasVisitConsent = consentMethod === 'biometric' ? Boolean(claim?.authorizationCode) : hasVisitStartOtp
    const hasFinalConsent = consentMethod === 'biometric' ? Boolean(claim?.dischargeAuthorizationCode || claim?.serviceType !== 'INPATIENT') : (claim?.serviceType !== 'INPATIENT' || hasDischargeOtp)
    const emergencyDeadline = claim?.emergency?.submissionDeadline ? new Date(claim.emergency.submissionDeadline) : null
    const emergencyRemainingMs = emergencyDeadline ? emergencyDeadline.getTime() - Date.now() : null
    const emergencyOverdue = emergencyRemainingMs !== null && emergencyRemainingMs < 0
    const canReopen = claim?.status === 'rejected'
    const isEmergency = claim?.serviceType === 'EMERGENCY'
    const emergencyProtocolLines = claim?.emergency?.protocolLines ?? []
    const canDiscard = claim?.status === 'draft' && !claim?.discarded

    return (
        <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-4">
            <div className="flex items-center justify-between border-b-2 border-gray-200 w-full pb-2">
                <p className="font-medium text-sm mb-0">Insurance Claim</p>
                <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${state.classes}`}>
                    {state.label}
                </span>
            </div>
            <div className="pt-3">
                <p className="text-sm text-gray-700 mb-2">{state.description}</p>
                <div className="grid gap-2 md:grid-cols-2 sm:grid-cols-1">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Insurer</p>
                        <p className="text-sm font-medium mb-0">{claim?.insurerData?.name ?? 'SHA-linked insurer'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Service Type</p>
                        <p className="text-sm font-medium mb-0">{claim?.serviceType ?? '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Resolved Scenario</p>
                        <p className="text-sm font-medium mb-0">{claim?.resolvedScenario ?? 'Not yet resolved'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Authorization Code</p>
                        <p className="text-sm font-medium mb-0">{claim?.authorizationCode ?? 'Not yet captured'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Consent Session</p>
                        <p className="text-sm font-medium mb-0">{consentSession?.source ? `${consentSession.source} • ${consentStatusLabel}` : 'Not yet started'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Consent Method</p>
                        <p className="text-sm font-medium mb-0">{consentMethod === 'biometric' ? 'Biometric / eKYC' : 'OTP'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                        <p className="text-sm font-medium mb-0">{claim?.updatedOn ? new Date(claim.updatedOn).toLocaleString() : '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Claim Lines</p>
                        <p className="text-sm font-medium mb-0">{lineSummary?.totalLines ?? 0}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                        <p className="text-sm font-medium mb-0">Ksh. {lineSummary?.totalAmount ?? 0}</p>
                    </div>
                </div>
                {
                    isEmergency && emergencyDeadline &&
                    <div className={`mt-3 rounded-sm border px-3 py-3 ${emergencyOverdue ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                        <p className={`text-xs font-semibold mb-1 ${emergencyOverdue ? 'text-red-700' : 'text-yellow-800'}`}>Emergency claim deadline</p>
                        <p className={`text-sm mb-0 ${emergencyOverdue ? 'text-red-700' : 'text-yellow-800'}`}>
                            Submit by {emergencyDeadline.toLocaleString()}
                            {emergencyRemainingMs !== null
                                ? emergencyOverdue
                                    ? ' • Deadline passed'
                                    : ` • ${Math.max(1, Math.ceil(emergencyRemainingMs / (1000 * 60 * 60)))} hour(s) remaining`
                                : ''}
                        </p>
                    </div>
                }
                {
                    isEmergency &&
                    <div className="mt-3 rounded-sm border border-orange-200 bg-orange-50 px-3 py-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <p className="text-xs font-semibold text-orange-800 mb-0">Emergency workflow</p>
                            <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${claim?.emergency?.identified ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                {claim?.emergency?.identified ? 'Patient identified' : 'Patient still unknown'}
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-orange-900 mb-1">Primary intervention code: {emergencyInterventionCode || 'Not yet captured'}</p>
                                <p className="text-xs text-orange-800 mb-0">Doctor consent and protocol lines should be completed before final submission.</p>
                            </div>

                            {
                                !claim?.emergency?.identified &&
                                <div className="rounded-sm border border-orange-200 bg-white px-3 py-3">
                                    <p className="text-xs font-semibold text-orange-800 mb-2">Identify this patient in SHA</p>
                                    <label className="block text-xs text-gray-500 mb-1">Reason patient is still unknown</label>
                                    <select
                                        value={unknownReason}
                                        onChange={e => setUnknownReason(e.target.value)}
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white mb-2">
                                        <option value="SHA_UNREGISTERED">Not yet found in SHA</option>
                                        <option value="DECEASED">Deceased</option>
                                    </select>
                                    <div className="flex justify-end mb-3">
                                        <button
                                            disabled={updatingUnknownReason}
                                            onClick={updateUnknownReason}
                                            className="btn bg-white border border-orange-200 hover:bg-orange-100 text-orange-800">
                                            Save Unknown Reason
                                        </button>
                                    </div>

                                    <label className="block text-xs text-gray-500 mb-1">Search patient</label>
                                    <Select
                                        value={selectedPatient}
                                        onInputChange={setPatientString}
                                        onChange={setSelectedPatient}
                                        isLoading={patientsLoading}
                                        options={patientOptions}
                                        placeholder="Search patient by name / ID"
                                    />

                                    <label className="block text-xs text-gray-500 mt-3 mb-1">Patient OTP</label>
                                    <input
                                        type="text"
                                        value={emergencyOtp}
                                        onChange={e => setEmergencyOtp(e.target.value)}
                                        placeholder="Enter OTP used to identify patient"
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                    />

                                    <div className="flex justify-end mt-3">
                                        <button
                                            disabled={!selectedPatient || !emergencyOtp.trim() || identifyingEmergency}
                                            onClick={identifyEmergencyPatient}
                                            className="btn hisani-btn-orange text-white">
                                            Identify Patient
                                        </button>
                                    </div>
                                </div>
                            }

                            <div className="rounded-sm border border-orange-200 bg-white px-3 py-3">
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <p className="text-xs font-semibold text-orange-800 mb-0">Emergency protocol lines</p>
                                    <button
                                        disabled={!emergencyInterventionCode || protocolsLoading}
                                        onClick={fetchEmergencyProtocols}
                                        className="btn bg-white border border-orange-200 hover:bg-orange-100 text-orange-800">
                                        Refresh Protocols
                                    </button>
                                </div>

                                {
                                    emergencyProtocolLines.length > 0 &&
                                    <div className="mb-3">
                                        {
                                            emergencyProtocolLines.map((line, index) =>
                                                <p key={ `${line.protocolCode}-${index}` } className="text-sm text-gray-700 mb-1">
                                                    • {line.protocolCode} ({line.interventionCode}) — Qty {line.quantity} @ Ksh. {line.unitPrice}
                                                </p>
                                            )
                                        }
                                    </div>
                                }

                                <label className="block text-xs text-gray-500 mb-1">Available SHA protocol</label>
                                <Select
                                    value={selectedProtocol}
                                    onChange={option => {
                                        setSelectedProtocol(option)
                                        if (option?.unit_price !== undefined) setProtocolUnitPrice(String(option.unit_price))
                                    }}
                                    isLoading={protocolsLoading}
                                    options={protocols}
                                    placeholder={ emergencyInterventionCode ? 'Select emergency protocol' : 'No intervention code available yet' }
                                />

                                <div className="grid gap-3 md:grid-cols-2 mt-3">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={protocolQuantity}
                                            onChange={e => setProtocolQuantity(e.target.value)}
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Unit Price</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={protocolUnitPrice}
                                            onChange={e => setProtocolUnitPrice(e.target.value)}
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-3">
                                    <button
                                        disabled={!claim?.consentToken || resendingDoctorConsent}
                                        onClick={resendEmergencyDoctorConsent}
                                        className="btn bg-white border border-orange-200 hover:bg-orange-100 text-orange-800">
                                        Resend Doctor Consent
                                    </button>
                                    <button
                                        disabled={!selectedProtocol || !protocolQuantity || addingProtocol}
                                        onClick={addEmergencyProtocolLine}
                                        className="btn hisani-btn-orange text-white">
                                        Add Protocol Line
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                {
                    perDiemSummary?.active && perDiemSummary?.blockers?.length > 0 &&
                    <div className="mt-3 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">Per-diem validation blockers</p>
                        {perDiemSummary.blockers.map(blocker => <p key={ blocker } className="text-sm text-yellow-800 mb-0">• {blocker}</p>)}
                    </div>
                }
                <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-700 mb-0">Consent checkpoints</p>
                        <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${hasVisitConsent && hasFinalConsent ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                            {hasVisitConsent && hasFinalConsent ? 'Complete' : 'Action needed'}
                        </span>
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium mb-0">{consentMethod === 'biometric' ? 'Visit-start biometric authorization' : 'Visit-start OTP'}</p>
                                <p className="text-xs text-gray-500 mb-0">
                                    {consentMethod === 'biometric'
                                        ? claim?.authorizationCode
                                            ? `Authorized${consentSession?.biometricMatchedAt ? ` on ${new Date(consentSession.biometricMatchedAt).toLocaleString()}` : ''}`
                                            : 'Not yet authorized'
                                        : hasVisitStartOtp
                                            ? `Captured${claim?.visitStartOtpCapturedAt ? ` on ${new Date(claim.visitStartOtpCapturedAt).toLocaleString()}` : ''}`
                                            : 'Not yet captured'}
                                </p>
                            </div>
                            <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${hasVisitConsent ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                {hasVisitConsent ? 'Ready' : 'Blocked'}
                            </span>
                        </div>
                        {
                            claim?.serviceType === 'INPATIENT' &&
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium mb-0">{consentMethod === 'biometric' ? 'Final discharge biometric authorization' : 'Final discharge OTP'}</p>
                                    <p className="text-xs text-gray-500 mb-0">
                                        {consentMethod === 'biometric'
                                            ? claim?.dischargeAuthorizationCode
                                                ? 'Authorized and ready for claim closure'
                                                : 'Still required before claim discharge/closure'
                                            : hasDischargeOtp
                                                ? `Captured${claim?.dischargeOtpCapturedAt ? ` on ${new Date(claim.dischargeOtpCapturedAt).toLocaleString()}` : ''}`
                                                : 'Still required before claim discharge/closure'}
                                    </p>
                                </div>
                                <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${hasFinalConsent ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                    {hasFinalConsent ? 'Ready' : 'Blocked'}
                                </span>
                            </div>
                        }
                    </div>
                    {
                        visitConsentStep?.blockers?.length > 0 &&
                        <div className="mt-2">
                            {
                                visitConsentStep.blockers.slice(0, 2).map((blocker, index) =>
                                    <p key={ `visit-consent-${index}` } className="text-xs text-red-600 mb-0">• {blocker}</p>
                                )
                            }
                        </div>
                    }
                    {
                        closeClaimStep?.blockers?.length > 0 &&
                        <div className="mt-2">
                            <p className="text-xs font-medium text-yellow-800 mb-1">Finalization blockers</p>
                            {
                                closeClaimStep.blockers.slice(0, 3).map((blocker, index) =>
                                    <p key={ `close-claim-${index}` } className="text-xs text-yellow-800 mb-0">• {blocker}</p>
                                )
                            }
                        </div>
                    }
                </div>
                {
                    consentSession &&
                    <div className="mt-3 rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <p className="text-xs font-semibold text-indigo-800 mb-0">SHA consent lifecycle</p>
                            <span className="inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs bg-white text-indigo-700 border-indigo-200">
                                {consentStatusLabel}
                            </span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-indigo-800 mb-0">
                                {consentMethod === 'biometric'
                                    ? consentSession?.biometricAuthGuid
                                        ? `Biometric auth GUID: ${consentSession.biometricAuthGuid}`
                                        : 'Biometric authorization session started'
                                    : consentSession?.phoneNumber
                                        ? `OTP channel: ${consentSession.phoneNumber}`
                                        : 'OTP channel captured'}
                            </p>
                            <p className="text-xs text-indigo-700 mb-0">
                                {consentSession?.consentId ? `Consent ID: ${consentSession.consentId}` : 'Consent ID pending'}
                                {consentSession?.visitId ? ` • Visit ID: ${consentSession.visitId}` : ''}
                            </p>
                            <p className="text-xs text-indigo-700 mb-0">
                                Started {consentSession?.date ? new Date(consentSession.date).toLocaleString() : '—'}
                                {consentSession?.expiresAt ? ` • Expires ${new Date(consentSession.expiresAt).toLocaleTimeString()}` : ''}
                            </p>
                            {
                                consentMethod === 'biometric' && consentSession?.biometricStatus &&
                                <p className="text-xs text-indigo-700 mb-0">
                                    Biometric status: {consentSession.biometricStatus}
                                    {consentSession?.biometricIframeUrl ? ' • Fingerprint/eKYC session is available for launch' : ''}
                                </p>
                            }
                            {
                                consentSession?.eligibilityStatus &&
                                <p className="text-xs text-indigo-700 mb-0">
                                    Eligibility snapshot: {consentSession.eligibilityStatus}
                                    {consentSession?.eligibilityCheckedAt ? ` • checked ${new Date(consentSession.eligibilityCheckedAt).toLocaleString()}` : ''}
                                </p>
                            }
                            {
                                consentStatus?.payload &&
                                <p className="text-xs text-indigo-700 mb-0">
                                    Latest upstream message: {consentStatus?.payload?.message ?? consentStatus?.payload?.status ?? 'Available'}
                                </p>
                            }
                            {
                                consentSession?.eligibilityOverrideReason &&
                                <div className="mt-2 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                                    <p className="text-xs font-semibold text-yellow-800 uppercase mb-1">Manual eligibility override</p>
                                    <p className="text-sm text-yellow-900 mb-1">{consentSession.eligibilityOverrideReason}</p>
                                    <p className="text-xs text-yellow-800 mb-0">
                                        Prior status: {consentSession?.eligibilityOverrideStatus ?? 'unknown'}
                                        {consentSession?.eligibilityOverrideAt ? ` • captured ${new Date(consentSession.eligibilityOverrideAt).toLocaleString()}` : ''}
                                    </p>
                                </div>
                            }
                        </div>
                        <div className="mt-3 flex flex-wrap justify-end gap-2">
                            <button
                                disabled={checkingConsent || refreshingConsent || closingConsent}
                                onClick={checkConsentStatus}
                                className="btn bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700">
                                Check Status
                            </button>
                            <button
                                disabled={!consentVisitOpen || checkingConsent || refreshingConsent || closingConsent}
                                onClick={refreshClaimConsent}
                                className="btn bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-700">
                                Refresh Consent
                            </button>
                            <button
                                disabled={!consentVisitOpen || checkingConsent || refreshingConsent || closingConsent}
                                onClick={closeClaimConsent}
                                className="btn bg-indigo-600 hover:bg-indigo-700 text-white">
                                Close Consent
                            </button>
                        </div>
                    </div>
                }
                {
                    executionSummary?.steps?.length > 0 &&
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-3">
                        <p className="text-xs font-semibold text-blue-800 mb-1">Execution history</p>
                        <p className="text-sm text-blue-800 mb-2">
                            Last execution {executionSummary?.latestExecutionAt ? new Date(executionSummary.latestExecutionAt).toLocaleString() : '—'}
                            {executionSummary?.latestExternalClaimId ? ` • Ref ${executionSummary.latestExternalClaimId}` : ''}
                        </p>
                        <div className="space-y-2">
                            {
                                executionSummary.steps.slice(0, 6).map(step =>
                                    <div key={ `${step.key}-${step.requestedAt}` } className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-medium mb-0">{step.key.replace(/_/g, ' ')}</p>
                                            <p className="text-xs text-blue-700 mb-0">{step.endpoint}</p>
                                            <p className="text-xs text-blue-700 mb-0">
                                                {step.payloadCount} payload{step.payloadCount === 1 ? '' : 's'} • {step.requestedAt ? new Date(step.requestedAt).toLocaleString() : '—'}
                                            </p>
                                        </div>
                                        <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${step.status === 'queued' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-green-100 text-green-700 border-green-200'}`}>
                                            {step.status}
                                        </span>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                }
                {
                    submissionPlan &&
                    <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-700 mb-0">Submission plan</p>
                            <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${submissionPlan?.readyToSubmit ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                {submissionPlan?.readyToSubmit ? 'Ready' : 'Blocked'}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {
                                planSteps.map(step =>
                                    <div key={ step.key } className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-medium mb-0">{step.label}</p>
                                            <p className="text-xs text-gray-500 mb-0">{step.endpoint}</p>
                                            {
                                                (step.blockers ?? []).slice(0, 2).map((blocker, index) =>
                                                    <p key={ `${step.key}-${index}` } className="text-xs text-red-600 mb-0">• {blocker}</p>
                                                )
                                            }
                                        </div>
                                        <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${step.ready ? 'bg-green-100 text-green-700 border-green-200' : step.blocking ? 'bg-red-100 text-red-700 border-red-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                            {step.ready ? 'Ready' : step.blocking ? 'Blocked' : 'Pending'}
                                        </span>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                }
                {
                    lineSummary &&
                    <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Claim preview readiness</p>
                        {
                            lineSummary?.lines?.length > 0 ?
                                <div className="space-y-2">
                                    {
                                        lineSummary.lines.slice(0, 5).map(line =>
                                            <div key={ line.lineItem } className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-medium mb-0">{line.name}</p>
                                                    <p className="text-xs text-gray-500 mb-0">
                                                        Qty {line.quantity} • Unit Ksh. {line.unitPrice} • Total Ksh. {line.totalAmount}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mb-0">
                                                        Intervention codes: {line.interventionCodes?.length > 0 ? line.interventionCodes.map(code => code.code).join(', ') : 'Missing'}
                                                    </p>
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        lineSummary.lines.length > 5 &&
                                        <p className="text-xs text-gray-500 mb-0">+ {lineSummary.lines.length - 5} more line(s)</p>
                                    }
                                </div>
                                :
                                <p className="text-sm text-gray-500 mb-0">No claim-ready billing lines have been linked yet.</p>
                        }
                    </div>
                }
                {
                    claim?.rejectionReason &&
                    <div className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2">
                        <p className="text-xs font-semibold text-red-700 mb-1">Rejection Reason</p>
                        <p className="text-sm text-red-700 mb-0">{claim.rejectionReason}</p>
                    </div>
                }
                {
                    hasPreauthBlockers &&
                    <div className="mt-3 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-2">Preauthorization blockers</p>
                        {
                            missingPreauth.length > 0 &&
                            <div className="mb-2">
                                <p className="text-xs font-medium text-yellow-800 mb-1">Missing preauth</p>
                                {
                                    missingPreauth.map(item =>
                                        <p key={ item.lineItem } className="text-sm text-yellow-800 mb-0">
                                            • {item.name} ({item.category})
                                        </p>
                                    )
                                }
                            </div>
                        }
                        {
                            unapprovedPreauth.length > 0 &&
                            <div>
                                <p className="text-xs font-medium text-yellow-800 mb-1">Not yet SHA-finalised</p>
                                {
                                    unapprovedPreauth.map(item =>
                                        <p key={ item.lineItem } className="text-sm text-yellow-800 mb-0">
                                            • {item.name} ({item.category}){item.referenceNumber ? ` — ${item.referenceNumber}` : ''}{item.status ? ` [${item.status}]` : ''}
                                        </p>
                                    )
                                }
                            </div>
                        }
                        <p className="text-xs text-yellow-800 mt-2 mb-0">
                            Next step: attach a finalised same-visit preauth from billing, or move elective follow-up work to the SHA preauthorization workbench.
                        </p>
                    </div>
                }
                {
                    hasCodeBlockers &&
                    <div className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-3">
                        <p className="text-xs font-semibold text-red-700 mb-2">Missing intervention codes</p>
                        {
                            missingCodes.map(item =>
                                <p key={ item.lineItem } className="text-sm text-red-700 mb-0">
                                    • {item.name} — missing {item.missingSystems?.join(', ')}
                                </p>
                            )
                        }
                    </div>
                }
                {
                    lineSummary?.diagnosisGap &&
                    <div className="mt-3 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">Diagnosis readiness</p>
                        <p className="text-sm text-yellow-800 mb-0">{lineSummary.diagnosisGap}</p>
                        <p className="text-xs text-yellow-800 mt-2 mb-0">Next step: return to the consultation record and save a primary ICD-11 diagnosis before submitting this claim.</p>
                    </div>
                }
                {
                    diagnoses.length > 0 &&
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-3">
                        <p className="text-xs font-semibold text-blue-800 mb-1">Diagnoses linked</p>
                        <p className="text-sm text-blue-800 mb-1">{diagnoses.length} diagnosis entry{diagnoses.length === 1 ? '' : 'ies'} available for mapping.</p>
                        <p className="text-xs text-blue-700 mb-0">Claim diagnosis payloads now use the stored ICD-11 diagnosis codes linked to each visit line.</p>
                    </div>
                }
                {
                    attachmentSummary &&
                    <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Attachment readiness</p>
                        <p className="text-sm text-gray-700 mb-2">
                            {attachmentSummary?.taggedAttachments ?? 0} claim-tagged attachment{attachmentSummary?.taggedAttachments === 1 ? '' : 's'} ready across {attachmentSummary?.readyPayloads ?? 0} file upload payload{attachmentSummary?.readyPayloads === 1 ? '' : 's'}.
                        </p>
                        {
                            taggedAttachments.length > 0 ?
                                <div className="space-y-2">
                                    {
                                        taggedAttachments.slice(0, 4).map(attachment =>
                                            <div key={ attachment.id }>
                                                <p className="text-sm font-medium mb-0">{attachment.title}</p>
                                                <p className="text-xs text-gray-500 mb-0">
                                                    {attachment.shaDocumentType}{attachment.interventionCode ? ` • ${attachment.interventionCode}` : ''} • {attachment.documents?.length ?? 0} file(s)
                                                </p>
                                            </div>
                                        )
                                    }
                                    {
                                        taggedAttachments.length > 4 &&
                                        <p className="text-xs text-gray-500 mb-0">+ {taggedAttachments.length - 4} more tagged attachment(s)</p>
                                    }
                                </div>
                                :
                                <p className="text-sm text-gray-500 mb-0">No patient attachments have been tagged for SHA claim upload yet.</p>
                        }
                    </div>
                }
                {
                    recommendedMissingAttachments.length > 0 &&
                    <div className="mt-3 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">Recommended claim documents still missing</p>
                        <p className="text-sm text-yellow-800 mb-0">{recommendedMissingAttachments.join(', ')}</p>
                        <p className="text-xs text-yellow-800 mt-2 mb-0">Next step: upload and tag the missing patient documents before final claim execution where payer rules require them.</p>
                    </div>
                }
                {
                    payloadPreview &&
                    <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Postman-aligned payload preview</p>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Start visit preview</p>
                        <p className="text-xs text-gray-500 mb-2">{payloadPreview?.startVisit?.endpoint}</p>
                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-3">{JSON.stringify(payloadPreview?.startVisit?.body, null, 2)}</pre>

                        <p className="text-xs text-gray-500 mb-2">{payloadPreview?.preview?.endpoint}</p>
                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-3">{JSON.stringify(payloadPreview?.preview?.body, null, 2)}</pre>

                        <p className="text-xs font-semibold text-gray-700 mb-1">Close claim preview</p>
                        <p className="text-xs text-gray-500 mb-2">{payloadPreview?.closeClaim?.endpoint}</p>
                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-3">{JSON.stringify(payloadPreview?.closeClaim?.body, null, 2)}</pre>

                        <p className="text-xs font-semibold text-gray-700 mb-1">Claim lines ({payloadPreview?.lines?.length ?? 0})</p>
                        {
                            payloadPreview?.lines?.length > 0 ?
                                payloadPreview.lines.slice(0, 3).map((line, index) =>
                                    <div key={ `${line?.meta?.lineItem}-${index}` } className="mb-2">
                                        <p className="text-xs text-gray-500 mb-1">{line.endpoint}</p>
                                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-0">{JSON.stringify(line.body, null, 2)}</pre>
                                    </div>
                                )
                                :
                                <p className="text-xs text-gray-500 mb-2">No claim-line payloads are ready yet.</p>
                        }
                        {
                            (payloadPreview?.lines?.length ?? 0) > 3 &&
                            <p className="text-xs text-gray-500 mb-3">+ {(payloadPreview?.lines?.length ?? 0) - 3} more line payload(s)</p>
                        }

                        <p className="text-xs font-semibold text-gray-700 mb-1">Attachments ({payloadPreview?.attachments?.length ?? 0})</p>
                        {
                            payloadPreview?.attachments?.length > 0 ?
                                payloadPreview.attachments.slice(0, 3).map((attachment, index) =>
                                    <div key={ `${attachment?.meta?.attachment}-${index}` } className="mb-2">
                                        <p className="text-xs text-gray-500 mb-1">{attachment.endpoint}</p>
                                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-0">{JSON.stringify(attachment.body, null, 2)}</pre>
                                    </div>
                                )
                                :
                                <p className="text-xs text-gray-500 mb-2">No attachment payloads are ready yet.</p>
                        }
                        {
                            (payloadPreview?.attachments?.length ?? 0) > 3 &&
                            <p className="text-xs text-gray-500 mb-3">+ {(payloadPreview?.attachments?.length ?? 0) - 3} more attachment payload(s)</p>
                        }

                        <p className="text-xs font-semibold text-gray-700 mb-1">Diagnoses ({payloadPreview?.diagnoses?.length ?? 0})</p>
                        {
                            payloadPreview?.diagnoses?.length > 0 ?
                                payloadPreview.diagnoses.slice(0, 3).map((diagnosis, index) =>
                                    <div key={ `${diagnosis?.meta?.diagnosis}-${index}` } className="mb-2">
                                        <p className="text-xs text-gray-500 mb-1">{diagnosis.endpoint}</p>
                                        <pre className="text-xs bg-white rounded p-2 overflow-x-auto mb-0">{JSON.stringify(diagnosis.body, null, 2)}</pre>
                                    </div>
                                )
                                :
                                <p className="text-xs text-gray-500 mb-0">No diagnosis payloads are ready yet.</p>
                        }
                        {
                            payloadPreview?.warnings?.length > 0 &&
                            <div className="mt-3">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Warnings</p>
                                {
                                    payloadPreview.warnings.map((warning, index) =>
                                        <p key={ `${warning.type}-${index}` } className="text-xs text-yellow-800 mb-0">
                                            • {warning.message}
                                        </p>
                                    )
                                }
                            </div>
                        }
                    </div>
                }
                {
                    canDiscard &&
                    <div className="mt-3 space-y-2 rounded-sm border border-red-200 bg-red-50 px-3 py-3">
                        <p className="text-xs font-semibold text-red-700 mb-1">Discard draft claim</p>
                        <p className="text-xs text-red-700 mb-0">Use this only when the SHA claim was started in error and should be closed out before submission.</p>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Discard reason type</label>
                            <input
                                type="text"
                                value={discardReasonType}
                                onChange={e => setDiscardReasonType(e.target.value)}
                                placeholder="e.g. duplicate-start, wrong-patient, wrong-scheme"
                                className="appearance-none block w-full bg-white text-gray-700 border border-red-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Discard reason details</label>
                            <textarea
                                rows={3}
                                value={discardReasonText}
                                onChange={e => setDiscardReasonText(e.target.value)}
                                placeholder="Capture what happened so the team has an audit trail"
                                className="appearance-none block w-full bg-white text-gray-700 border border-red-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                disabled={transitioning || executing || rejecting || reopening || refreshingConsent || closingConsent || discarding || !discardReasonType.trim() || !discardReasonText.trim()}
                                onClick={discardClaim}
                                className="btn bg-red-600 hover:bg-red-700 text-white">
                                Discard Claim
                            </button>
                        </div>
                    </div>
                }
                {
                    canResolve &&
                    <div className="mt-3 space-y-2">
                        <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                            <p className="text-xs font-semibold text-yellow-800 mb-1">Manual payer reconciliation</p>
                            <p className="text-sm text-yellow-800 mb-0">Use these actions only after you have confirmed the payer decision from SHA or the remittance/adjudication advice.</p>
                        </div>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Payer rejection reason</label>
                            <input
                                type="text"
                                value={rejectionReason}
                                onChange={e => setRejectionReason(e.target.value)}
                                placeholder="Reason if rejecting claim"
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                disabled={transitioning || executing || rejecting || refreshingConsent || closingConsent}
                                onClick={transitionClaim}
                                className="btn hisani-btn-green-light text-white">
                                Record Payer Acceptance
                            </button>
                            <button
                                disabled={transitioning || executing || rejecting || refreshingConsent || closingConsent || !rejectionReason.trim()}
                                onClick={rejectClaim}
                                className="btn bg-red-600 hover:bg-red-700 text-white">
                                Record Payer Rejection
                            </button>
                        </div>
                    </div>
                }
                {
                    canReopen &&
                    <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Exceptional recovery</p>
                        <div className="flex justify-end">
                            <button
                                disabled={transitioning || executing || rejecting || reopening || refreshingConsent || closingConsent || discarding}
                                onClick={reopenClaim}
                                className="btn bg-slate-700 hover:bg-slate-800 text-white">
                                Return to Draft
                            </button>
                        </div>
                    </div>
                }
                {
                    canSubmit &&
                    <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Primary operational next step</p>
                        <div className="flex justify-end">
                            <button
                                disabled={transitioning || executing || rejecting || reopening || refreshingConsent || closingConsent || discarding || previewLoading || hasPreauthBlockers || hasCodeBlockers || hasDiagnosisBlockers || hasAttachmentBlockers || submissionPlan?.readyToSubmit === false}
                                onClick={executeClaimSubmission}
                                className="btn hisani-btn-green-light text-white">
                                Execute Claim Submission
                            </button>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default ClaimStatusCard
